import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, ViewChild, inject } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import {
  ButtonComponent,
  MbsSize,
  ModalComponent,
  ModalFooterDirective,
  SharedPersistentStateEnum,
  SortEvent,
  TableCellDirective,
  TableGridComponent,
  TableHeader,
  TextEllipsisComponent
} from 'mbs-ui-kit';
import { LicensesFacade } from 'mbs-ui-shared@facades/licenses.facade';
import { Administrator, LicenseType } from 'mbs-ui-shared@models';
import { AdministratorLicense } from 'mbs-ui-shared@models/License';
import { LicenseSummary } from 'mbs-ui-shared@models/licenses/license-summary';
import { Observable, of, take } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { POST_PAYMENT_TOKEN } from '../../../../tokens/post-payment.token';
import { AssignLicenseDialogueData } from './assign-license-dialogue-data';

@UntilDestroy()
@Component({
  selector: 'mbs-assign-license-dialogue',
  templateUrl: './assign-license-dialogue.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    I18NextPipe,
    ModalComponent,
    TableGridComponent,
    TextEllipsisComponent,
    TableCellDirective,
    ModalFooterDirective
  ],
  styles: []
})
export class AssignLicenseDialogueComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private ability = inject(AbilityService);
  licensesFacade = inject(LicensesFacade);
  isPostPayment = inject<Observable<boolean>>(POST_PAYMENT_TOKEN);

  public orderBy: SortEvent;
  public settings: AssignLicenseDialogueData = null;

  public licenses: LicenseSummary[] = [];
  private entityId = '';

  public readonly size = MbsSize;
  public readonly sharedPersistentStateEnum = SharedPersistentStateEnum;

  public headers: TableHeader[] = [
    { name: 'Type', gridColSize: '5fr' },
    { name: 'Available', class: '-end', gridColSize: '3fr' },
    { name: '', class: '-end', gridColSize: '2fr' }
  ];
  private user: Administrator;
  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;

  ngOnInit(): void {
    this.isPostPayment.pipe(take(1)).subscribe((isPostPayment) => {
      if (isPostPayment) {
        const index = this.headers.findIndex((header) => header.name === 'Available');
        this.headers.splice(index, 1);
      }
    });

    if (this.baseModal.data) {
      const data = this.baseModal.data as AssignLicenseDialogueData;
      this.settings = data;
      this.entityId = data.entityId;

      if (this.settings.showTrials) {
        this.headers[this.headers.length - 1].name = 'License';
        this.headers.push({ name: 'Trial', class: '-end', gridColSize: '2fr' });
      }
    }
    if (!this.entityId) {
      throw new Error('You must provide entityId in the data property that is being set in the modal settings');
    }
    this.licensesFacade
      .getAvailableLicenseSummaries()
      .pipe(
        switchMap((licenses) => {
          if (this.settings.licenseTypesFilter && this.settings.licenseTypesFilter.length > 0) {
            licenses = licenses.filter((x) => this.settings.licenseTypesFilter.includes(x.licenseType));
          }
          if (this.settings.filterLicensesFunc$) return this.settings.filterLicensesFunc$(this.entityId, [...licenses]);
          return of(licenses);
        })
      )
      .subscribe({
        next: (licenses) => {
          this.licenses = licenses;
          if (!licenses.some((l) => l.canTrial)) this.settings.showTrials = false;
        }
      });
    this.cdr.detectChanges();
  }

  handleSort(sortEvent: SortEvent): void {
    this.orderBy = sortEvent;
  }

  grantLicense(license: AdministratorLicense, trial = false): void {
    this.licensesFacade
      .grantLicense(license.licenseType, this.entityId, trial)
      .pipe(untilDestroyed(this))
      .subscribe((license) => {
        this.baseModal.save(license);
      });
  }

  cancelClickHandler(): void {
    this.baseModal.close();
  }

  buyNewClickHandler(): void {
    // quick hack for now, while we have only administrators licenses with this dialogue..
    // elsewise TODO: it should be calling service method that returns observable or Promise that will emit value and close
    // after the license is bought and returned here

    const type =
      this.settings?.licenseTypesFilter?.length === 1 && this.settings.licenseTypesFilter[0] === LicenseType.RMM ? 'rmm' : 'connect';

    this.licensesFacade.buyLicenseByType(type).then(() => this.baseModal.save());
  }
}
